import React, {memo, useState, useRef, useCallback, forwardRef, useMemo} from 'react';
import {
    View,
    TouchableOpacity,
    TextInput,
    Text as RNText,
} from 'react-native';
import {createUseStyle} from '@redlink/assets/styles';
import {Text, Space, IconClose} from '@redlink/components/atoms';

const PADDING_HORIZONTAL = 10,
    PADDING_VERTICAL = 10,
    TAG_VERTICAL_SPACE = 2,
    TAG_HORIZONTAL_SPACE = 4;

export const TagInput = memo(
    ({
         data = [],
         title,
         style,
         require = false,
         editable = true,
         onAdd,
         onRemove,
         description,
         boldTitle,
     }) => {
        const {styles, colors} = useStyle();
        const [value, setValue] = useState();
        const [isFocus, setFocus] = useState(false);
        const inputRef = useRef(null);
        const containerElementRef = useRef(null);
        const [containerLayout, setContainerLayout] = useState({});
        const [fakeInputLayout, setFakeInputLayout] = useState({});
        const [inputContainerLayout, setInputContainerLayout] = useState([]);

        const onFocus = () => editable && setFocus(true);
        const onBlur = () => setFocus(false);
        const onChangeText = (text) => setValue(text);
        const focus = () =>
            setTimeout(() => {
                inputRef.current.focus();
            }, 200);
        const onKeyPress = ({nativeEvent: {key}}) => {
            if (key === 'Enter') {
                if (value) {
                    onAdd && onAdd(value);
                    setValue('');
                }
                focus();
            }
        };
        const onTagRemove = (item) => {
            onRemove && onRemove(item);
            focus();
        };
        const onLayoutContainer = useCallback(() => {
            containerElementRef.current?.measure((fx, fy, width, height, px, py) => {
                setContainerLayout({x: px, y: py, width, height});
            })
        }, []);
        const onLayoutInputContainer = useCallback(({nativeEvent: {layout: {x, y, width, height}}}, index) => {
            setInputContainerLayout(prev => {
                const duplicateArray = [...prev];
                duplicateArray[index] = {x, y, width, height};
                return duplicateArray;
            });
        }, [inputContainerLayout]);
        const onLayoutFakeInput = useCallback(({nativeEvent: {layout: {x, y, width, height}}}) => {
            setFakeInputLayout({x: x, y: y, width, height});
        }, []);

        const realContainerSpace = useMemo(() => (containerLayout.width - PADDING_HORIZONTAL * 2) - TAG_HORIZONTAL_SPACE, [containerLayout]);
        const inputWidthTemp = useMemo(() => inputContainerLayout.reduce((prev, {width}) => {
            const actualItemWidth = (width + TAG_HORIZONTAL_SPACE);
            const remainSpace = prev - actualItemWidth;
            if (remainSpace < 0) return realContainerSpace - actualItemWidth;
            else return remainSpace;
        }, realContainerSpace), [inputContainerLayout, realContainerSpace]);
        const inputWidth = useMemo(() => inputWidthTemp < fakeInputLayout.width ? realContainerSpace : inputWidthTemp, [inputWidthTemp, fakeInputLayout, realContainerSpace]);

        return (
            <View style={styles.rootContainer}>
                <View style={styles.titlesContainer}>
                    <Text
                        style={[
                            styles.titleText,
                            boldTitle && styles.boldTitleText,
                            {
                                color: isFocus
                                    ? colors.blue1
                                    : boldTitle
                                        ? colors.black
                                        : colors.gray21,
                            },
                        ]}
                    >
                        {title}
                        {require && <Text style={styles.requireText}>*</Text>}
                    </Text>
                    <Space size={8}/>
                    <Text
                        style={[
                            styles.titleText,
                            {
                                color: isFocus ? colors.blue1 : colors.gray21,
                            },
                        ]}
                    >
                        {description}
                    </Text>
                </View>
                <Space size={4}/>
                <View
                    ref={containerElementRef}
                    onLayout={onLayoutContainer}
                    style={[
                        style,
                        styles.container,
                        {
                            borderColor: isFocus ? colors.blue1 : colors.black3,
                        },
                    ]}
                >
                    <View style={styles.inputContainer}>
                        {data.map((item, index) => (
                            <Tag
                                onLayout={(e) => onLayoutInputContainer(e, index)} value={item}
                                onPress={() => onTagRemove(item)}/>
                        ))}
                        <View style={styles.textInputContainer}>
                            <RNText onLayout={onLayoutFakeInput} style={styles.fakeInputText}>{value}</RNText>
                            {!value && <RNText> </RNText>}
                            <TextInput
                                ref={inputRef}
                                style={[styles.input, {width: inputWidth}]}
                                {...{onFocus, onBlur, value, onKeyPress, onChangeText}}
                            />
                        </View>
                    </View>
                </View>
            </View>
        );
    }
);

const Tag = memo(forwardRef(({value, onPress, onLayout}, ref) => {
    const {styles, colors} = useStyle();
    return (
        <>
            <TouchableOpacity
                {...{onPress, onLayout}}
                activeOpacity={0.7}
                style={styles.itemContainer}
            >
                <IconClose color={colors.white} size={10}/>
                <Space size={4}/>
                <Text style={styles.tagText} en>
                    {value}
                </Text>
            </TouchableOpacity>
            <Space size={TAG_HORIZONTAL_SPACE}/>
        </>
    );
}));

const useStyle = createUseStyle(
    ({colors, size, shadow, ltrFlexDirection, language, isRtl}) => ({
        rootContainer: {
            flex: 1,
        },
        titlesContainer: {
            flexDirection: 'row',
            alignItems: 'center',
        },
        itemContainer: {
            backgroundColor: colors.gray21,
            borderRadius: 5,
            padding: 3,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            marginVertical: TAG_VERTICAL_SPACE,
        },
        tagText: {
            color: colors.white,
            fontSize: 12,
            ...(language === 'fa' && {fontFamily: 'dana'}),
        },
        container: {
            backgroundColor: colors.white,
            borderWidth: 1,
            borderColor: colors.yellow1,
            paddingVertical: PADDING_VERTICAL,
            paddingHorizontal: PADDING_HORIZONTAL,
            borderRadius: 5,
        },
        titleText: {
            fontSize: 10,
        },
        inputContainer: {
            flex: 1,
            flexDirection: ltrFlexDirection,
            alignItems: 'center',
            flexWrap: 'wrap',
        },
        requireText: {
            color: colors.red1,
            fontSize: 10,
            marginStart: 5,
        },
        input: {
            ...(language === 'fa' && {fontFamily: 'dana'}),
            fontSize: 14,
            position: 'absolute',
            ...(isRtl && {right: 0}),
            ...(!isRtl && {left: 0}),
            textAlign: isRtl ? 'right' : 'left',
        },
        tagsContainer: {
            flexDirection: ltrFlexDirection,
            alignItems: 'center',
            flexWrap: 'wrap',
        },
        textInputContainer: {},
        fakeInputText: {
            minWidth: 65,
            fontSize: 14,
            color: 'transparent',
        },
        boldTitleText: {
            fontSize: 14,
        },
    })
);
